import { UnfoldMore } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
    AppBar,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    InputAdornment,
    ListSubheader,
    MenuItem,
    Modal,
    Popper,
    Select,
    Stack,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { MuiTextFieldWithFocus } from '../../config/MaterialUIConstants';
import { vehicleMake, vehicleModel, vehicleYear } from '../../redux/Vehicles/action';
import { useGetProvinceMasterList } from '../../services/core/master/master.hooks';
import { masterQueryOptions } from '../../utility/Constant';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
};

const PLATE_STYLES = {
    provinceSelect: {
        backgroundColor: '#fff',
        height: '40px',
        width: '68px',
        fontSize: '13px',
        letterSpacing: '0.15px',
        fontWeight: 400,
        color: '#212121',
        pl: 2,
        pr: 1,
        border: 'none',
        borderRadius: '4px 0 0 4px',
        display: 'flex',
        alignItems: 'center',
        lineHeight: '150%',
        '& svg': {
            color: 'rgba(0, 0, 0, 0.57)',
            stroke: 1,
            marginRight: '10px',
            width: '18px',
            height: '18px',
            marginTop: '2px',
        },
        '& .MuiSelect-select': {
            backgroundColor: '#fff !important',
            paddingLeft: 0,
            border: 'none',
        },
        '& .MuiSelect-select:focus': {
            backgroundColor: '#fff !important',
            border: 'none',
        },
    },
    menuPaper: {
        maxHeight: '350px',
        mb: 1,
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.10)',
        background: 'var(--White, #FFF)',
        boxShadow: '0 0 24px 0 rgba(0, 0, 0, 0.10)',
        overflow: 'hidden',
        padding: 0,
    },
    menuList: {
        maxHeight: '350px',
        overflowY: 'auto',
        padding: 0,
        pb: 1,
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '3px' },
        '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '3px', '&:hover': { background: '#a8a8a8' } },
    },
    searchHeader: {
        backgroundColor: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        padding: '8px 8px 4px 8px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.10)',
        borderRadius: '8px 8px 0 0',
    },
    countryHeader: {
        p: 0,
        pt: 1.5,
        pl: '14px',
        fontWeight: 500,
        fontSize: '18px',
        color: '#006CDD',
        lineHeight: '150%',
        letterSpacing: '0.15px',
        textTransform: 'uppercase',
        backgroundColor: 'white',
        position: 'sticky',
        top: '61px',
        zIndex: 10,
    },
    provinceMenuItem: {
        pl: '14px',
        backgroundColor: 'white',
        '&:hover': { backgroundColor: '#f5f5f5' },
    },
};

const labelSx = { fontWeight: 500, fontSize: '16px', lineHeight: '24px', color: '#000', mb: 0.5 };

const SelectVehicleModal = ({
    open,
    onCancel,
    onApply,
    yearData,
    makeData,
    modelData,
    licenseNumber,
    fleetUnitNumber,
    provinceId,
    vinFilter,
    showFleetUnit = true,
}) => {
    const [yearList, setYearList] = useState([]);
    const [makeList, setMakeList] = useState([]);
    const [modelList, setModelList] = useState([]);
    const [yearServicePageData, setYearServicePageData] = useState({ totalpages: '', currentPage: '' });
    const [makeServicePageData, setMakeServicePageData] = useState({ totalpages: '', currentPage: '' });
    const [modelServicePageData, setModelServicePageData] = useState({ totalpages: '', currentPage: '' });

    const [yearScrollPage, setYearScrollPage] = useState(1);
    const [makeScrollPage, setMakeScrollPage] = useState(1);
    const [modelScrollPage, setModelScrollPage] = useState(1);

    const [yearLoading, setYearLoading] = useState(false);
    const [makeLoading, setMakeLoading] = useState(false);
    const [modelLoading, setModelLoading] = useState(false);

    const [searchText, setSearchText] = useState('');
    const { data: provinces = [], isLoading: provinceLoader } = useGetProvinceMasterList({}, { ...masterQueryOptions });

    const groupedProvinces = useMemo(() => {
        if (!Array.isArray(provinces) || provinces.length === 0) return {};
        const shopCountryId = localStorage.getItem('shopCountryId') || '64e738882fec8f2c9b79c139';
        const filtered = provinces.filter((province) => {
            if (!province?.province || !province?.provinceCode) return false;
            const q = searchText.toLowerCase();
            return province.province.toLowerCase().includes(q) || province.provinceCode.toLowerCase().includes(q);
        });
        const grouped = filtered.reduce((acc, province) => {
            const countryName = province.country?.country || 'Unknown';
            if (!acc[countryName]) acc[countryName] = [];
            acc[countryName].push(province);
            return acc;
        }, {});
        const shopCountry = provinces.find((p) => p.country?._id === shopCountryId)?.country?.country;
        const ordered = {};
        if (shopCountry && grouped[shopCountry]) ordered[shopCountry] = grouped[shopCountry];
        Object.keys(grouped)
            .filter((c) => c !== shopCountry)
            .sort()
            .forEach((c) => {
                ordered[c] = grouped[c];
            });
        return ordered;
    }, [provinces, searchText]);

    const limit = 1000;
    const dispatch = useDispatch();

    const [draftYear, setDraftYear] = useState('');
    const [draftMake, setDraftMake] = useState('');
    const [draftModel, setDraftModel] = useState('');
    const [draftLicense, setDraftLicense] = useState('');
    const [draftUnit, setDraftUnit] = useState('');
    const [draftProvinceId, setDraftProvinceId] = useState('');
    const [draftVin, setDraftVin] = useState('');

    // Autocomplete input display values
    const [yearInputValue, setYearInputValue] = useState('');
    const [makeInputValue, setMakeInputValue] = useState('');
    const [modelInputValue, setModelInputValue] = useState('');

    useEffect(() => {
        if (!open) return;
        vehicleYearData(1, limit);
        vehicleMakeData(1, limit, draftYear);

        // initialize draft values from applied (saved) values every time modal opens
        setDraftYear(yearData || '');
        setDraftMake(makeData || '');
        setDraftModel(modelData || '');
        setDraftLicense(licenseNumber || '');
        setDraftUnit(fleetUnitNumber || '');
        setDraftVin(vinFilter || '');

        // Sync autocomplete input display values
        setYearInputValue(yearData ? String(yearData) : '');
        setMakeInputValue(''); // resolved via effect once makeList loads
        setModelInputValue(''); // resolved via effect once modelList loads

        // const savedProvince = provinceId || '';
        // if (savedProvince) {
        //     setDraftProvinceId(savedProvince);
        // } else if (provinces?.length > 0) {
        //     const defaultProvince = provinces.find((p) => p.isDefault === true)?._id || '';
        //     setDraftProvinceId(defaultProvince);
        // }

        // Fetch year list
        vehicleYearData(1, limit);

        // ✅ Fetch make list based on the SAVED year (not stale draftYear)
        vehicleMakeData(1, limit, yearData || '');

        // ✅ Fetch model list based on the SAVED make + year if both exist
        setModelList([]);
        setModelScrollPage(1);
        setModelServicePageData({ totalpages: '', currentPage: '' });
        if (makeData) vehicleModelData(1, limit, makeData, yearData || '');
    }, [open]);

    // Sync make label once makeList is available
    useEffect(() => {
        if (draftMake && makeList.length > 0) {
            const found = makeList.find((m) => m._id === draftMake);
            if (found) setMakeInputValue(found.name);
        }
    }, [makeList, draftMake]);

    // Sync model label once modelList is available
    useEffect(() => {
        if (draftModel && modelList.length > 0) {
            const found = modelList.find((m) => m._id === draftModel);
            if (found) setModelInputValue(found.name);
        }
    }, [modelList, draftModel]);

    // useEffect(() => {
    //     if (!open || !provinces?.length) return;
    //     // Only apply default if modal is open, provinces just loaded, and user has no saved province
    //     if (!provinceId && !draftProvinceId) {
    //         const defaultProvince = provinces.find((p) => p.isDefault === true)?._id || '';
    //         setDraftProvinceId(defaultProvince);
    //     }
    // }, [open, provinces]);

    const handleCancel = () => {
        // Revert all drafts back to the last saved/applied values
        setDraftYear(yearData || '');
        setDraftMake(makeData || '');
        setDraftModel(modelData || '');
        setDraftLicense(licenseNumber || '');
        setDraftUnit(fleetUnitNumber || '');
        setDraftProvinceId(provinceId || '');
        setDraftVin(vinFilter || '');
        setYearInputValue(yearData ? String(yearData) : '');
        setMakeInputValue(''); // re-synced via effect
        setModelInputValue(''); // re-synced via effect
        onCancel?.();
    };

    const vehicleYearData = async (page, pageLimit) => {
        setYearLoading(true);
        try {
            const data = await dispatch(vehicleYear(page, pageLimit));
            if (data?.data) {
                setYearList((prev) => (page === 1 ? [...data.data] : [...(Array.isArray(prev) ? prev : []), ...data.data]));
                setYearServicePageData({ totalpages: data?.totalPages, currentPage: data?.currentPage });
            }
        } finally {
            setYearLoading(false);
        }
    };

    const vehicleMakeData = async (page, pageLimit, year) => {
        setMakeLoading(true);
        try {
            console.log('🚀 ~ vehicleMake ~ year 1111:', year);
            const data = await dispatch(vehicleMake(page, pageLimit, year));
            if (data?.data) {
                setMakeList((prev) => (page === 1 ? [...data.data] : [...(Array.isArray(prev) ? prev : []), ...data.data]));
                setMakeServicePageData({ totalpages: data?.totalPages, currentPage: data?.currentPage });
            }
        } finally {
            setMakeLoading(false);
        }
    };

    const vehicleModelData = async (page, pageLimit, id, year) => {
        setModelLoading(true);
        try {
            const data = await dispatch(vehicleModel(page, pageLimit, id, year));
            if (data?.data) {
                setModelList((prev) => (page === 1 ? [...data.data] : [...(Array.isArray(prev) ? prev : []), ...data.data]));
                setModelServicePageData({ totalpages: data?.totalPages, currentPage: data?.currentPage });
            }
        } finally {
            setModelLoading(false);
        }
    };

    const handleResetAndApply = () => {
        const cleared = {
            yearData: '',
            makeData: '',
            modelData: '',
            licenseNumber: '',
            fleetUnitNumber: '',
            provinceId: '',
            vinFilter: '',
        };

        setDraftYear('');
        setDraftMake('');
        setDraftModel('');
        setDraftLicense('');
        setDraftUnit('');
        setDraftProvinceId('');
        setDraftVin('');
        setYearInputValue('');
        setMakeInputValue('');
        setModelInputValue('');
        setModelList([]);
        setModelScrollPage(1);
        setModelServicePageData({ totalpages: '', currentPage: '' });

        // Reset should close modal + run API with cleared payload
        onApply?.(cleared);
    };

    const handleLicencePlateChange = (event) => {
        setDraftLicense(event.target.value?.toUpperCase?.() ?? event.target.value);
    };

    const handleUnitChange = (event) => {
        setDraftUnit(event.target.value);
    };

    const handleVinChange = (event) => {
        setDraftVin(event.target.value?.toUpperCase?.() ?? event.target.value);
    };

    const handleYearChange = (newValue) => {
        setDraftYear(newValue || '');
        setYearInputValue(newValue ? String(newValue) : '');
        // Clear dependent fields
        setDraftMake('');
        setMakeInputValue('');
        setDraftModel('');
        setModelInputValue('');
        setModelList([]);
        setModelScrollPage(1);
        setModelServicePageData({ totalpages: '', currentPage: '' });
        if (newValue) vehicleMakeData(1, limit, newValue);
    };

    const handleMakeChange = (newValue) => {
        const id = newValue?._id || '';
        setDraftModel('');
        setModelInputValue('');
        setDraftMake(id);
        setMakeInputValue(newValue?.name || '');
        setModelList([]);
        setModelScrollPage(1);
        setModelServicePageData({ totalpages: '', currentPage: '' });
        if (id) vehicleModelData(1, limit, id, draftYear);
    };

    const handleModelChange = (newValue) => {
        setDraftModel(newValue?._id || '');
        setModelInputValue(newValue?.name || '');
    };

    const onSubmit = (e) => {
        e.preventDefault();
        onApply?.({
            yearData: draftYear,
            makeData: draftMake,
            modelData: draftModel,
            licenseNumber: draftLicense,
            fleetUnitNumber: draftUnit,
            provinceId: draftProvinceId,
            vinFilter: draftVin,
        });
    };

    const handleScroll = (e, status) => {
        const target = e.target;
        const bottom = Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 2;
        if (!bottom) return;

        if (status === 'year') {
            if (yearServicePageData.totalpages > yearScrollPage) {
                const next = yearScrollPage + 1;
                setYearScrollPage(next);
                vehicleYearData(next, limit);
            }
        } else if (status === 'make') {
            if (makeServicePageData.totalpages > makeScrollPage) {
                const next = makeScrollPage + 1;
                setMakeScrollPage(next);
                vehicleMakeData(next, limit, draftYear);
            }
        } else if (draftMake) {
            if (modelServicePageData.totalpages > modelScrollPage) {
                const next = modelScrollPage + 1;
                setModelScrollPage(next);
                vehicleModelData(next, limit, draftMake, draftYear);
            }
        }
    };

    const scrollToSelectedProvince = () => {
        setTimeout(() => {
            const menuContainer = document.querySelector('.MuiPaper-root.MuiPopover-paper');
            if (!menuContainer || !draftProvinceId) return;
            const selectedItem = menuContainer.querySelector(`[data-value="${draftProvinceId}"]`);
            selectedItem?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 100);
    };

    return (
        <Box>
            <Modal open={open} onClose={handleCancel} aria-labelledby="parent-modal-title" aria-describedby="parent-modal-description">
                <Box
                    sx={{
                        ...style,
                        height: 'fit-content',
                        width: { xs: '100%', sm: '480px' },
                        maxWidth: '96vw',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <AppBar
                        position="static"
                        variant="elevation"
                        sx={{
                            height: '64px',
                            flexShrink: 0,
                            paddingRight: '0px !important',
                            backgroundColor: 'white',
                            color: 'black',
                            boxShadow: 'none',
                            borderRadius: '8px 8px 0 0',
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        }}
                    >
                        <Toolbar variant="regular" sx={{ display: 'flex', minHeight: '64px !important' }}>
                            <Typography variant="h6" sx={{ flexGrow: 1, color: '#000', fontSize: '20px', fontWeight: 500 }}>
                                Filter by vehicle
                            </Typography>
                            <CloseIcon onClick={handleCancel} sx={{ cursor: 'pointer', fontSize: '32px', height: '32px', color: 'rgba(0, 0, 0, 0.87)' }} />
                        </Toolbar>
                    </AppBar>

                    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <Box sx={{ pt: 4, px: 3, pb: 1, width: '100%', boxSizing: 'border-box' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={showFleetUnit ? 6 : 12}>
                                    <Typography variant="body1" sx={labelSx}>
                                        Licence Plate #
                                    </Typography>
                                    <TextField
                                        placeholder="Search"
                                        value={draftLicense ?? ''}
                                        onChange={handleLicencePlateChange}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            ...MuiTextFieldWithFocus,
                                            '& .MuiOutlinedInput-root': { paddingLeft: 0, pl: 0 },
                                            '& .MuiOutlinedInput-input': { paddingLeft: '12px' },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start" sx={{ marginRight: 0 }}>
                                                    <FormControl>
                                                        <Select
                                                            value={draftProvinceId || ''}
                                                            displayEmpty
                                                            onChange={(e) => setDraftProvinceId(e.target.value)}
                                                            onClose={() => setSearchText('')}
                                                            onOpen={scrollToSelectedProvince}
                                                            variant="standard"
                                                            disableUnderline
                                                            IconComponent={UnfoldMore}
                                                            renderValue={(selected) => {
                                                                const p = provinces?.find((m) => m._id === selected);
                                                                return p?.provinceCode || '';
                                                            }}
                                                            sx={PLATE_STYLES.provinceSelect}
                                                            MenuProps={{
                                                                autoFocus: false,
                                                                anchorOrigin: { vertical: 'top', horizontal: 'left' },
                                                                transformOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                                PaperProps: { sx: PLATE_STYLES.menuPaper },
                                                                MenuListProps: { sx: PLATE_STYLES.menuList },
                                                            }}
                                                        >
                                                            <ListSubheader sx={PLATE_STYLES.searchHeader}>
                                                                <TextField
                                                                    autoFocus
                                                                    placeholder="Search Province or State"
                                                                    fullWidth
                                                                    size="small"
                                                                    value={searchText}
                                                                    onChange={(e) => setSearchText(e.target.value)}
                                                                    onKeyDown={(ev) => {
                                                                        if (ev.key !== 'Escape') ev.stopPropagation();
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <SearchIcon sx={{ color: 'rgba(110, 110, 110, 1)', mr: 1, fontSize: 18 }} />
                                                                        ),
                                                                    }}
                                                                    sx={MuiTextFieldWithFocus}
                                                                />
                                                            </ListSubheader>
                                                            {Object.entries(groupedProvinces).map(([country, list]) => [
                                                                <ListSubheader key={`h-${country}`} sx={PLATE_STYLES.countryHeader}>
                                                                    {country === 'United States' ? 'USA' : country.toUpperCase()}
                                                                </ListSubheader>,
                                                                ...list.map((province) => (
                                                                    <MenuItem value={province._id} key={province._id} sx={PLATE_STYLES.provinceMenuItem}>
                                                                        {province.province}
                                                                    </MenuItem>
                                                                )),
                                                            ])}
                                                            {Object.keys(groupedProvinces).length === 0 && !provinceLoader && (
                                                                <MenuItem disabled>No provinces found</MenuItem>
                                                            )}
                                                            {provinceLoader && (
                                                                <MenuItem disabled>
                                                                    <CircularProgress size={16} />
                                                                </MenuItem>
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                {showFleetUnit && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body1" sx={labelSx}>
                                            Unit #
                                        </Typography>
                                        <TextField
                                            placeholder="Search"
                                            value={draftUnit ?? ''}
                                            onChange={handleUnitChange}
                                            fullWidth
                                            size="small"
                                            sx={MuiTextFieldWithFocus}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ color: 'rgba(110, 110, 110, 1)' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.12)' }} />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body1" sx={labelSx}>
                                        VIN
                                    </Typography>
                                    <TextField
                                        placeholder=""
                                        value={draftVin ?? ''}
                                        onChange={handleVinChange}
                                        fullWidth
                                        size="small"
                                        inputProps={{ maxLength: 17 }}
                                        sx={MuiTextFieldWithFocus}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider textAlign="center" sx={{ width: '100%', my: 1 }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', px: 1, fontWeight: 500 }}>
                                            or
                                        </Typography>
                                    </Divider>
                                </Grid>

                                {/* Year */}
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="body1" sx={labelSx}>
                                        Year
                                    </Typography>
                                    <Autocomplete
                                        fullWidth
                                        disableClearable
                                        forcePopupIcon
                                        loading={yearLoading}
                                        options={yearList}
                                        getOptionLabel={(option) => (option ? String(option) : '')}
                                        value={draftYear || null}
                                        inputValue={yearInputValue}
                                        onChange={(event, newValue) => {
                                            handleYearChange(newValue);
                                        }}
                                        onInputChange={(event, newValue) => {
                                            setYearInputValue(newValue);
                                        }}
                                        ListboxProps={{
                                            onScroll: (e) => handleScroll(e, 'year'),
                                            style: { maxHeight: 220 },
                                        }}
                                        PopperComponent={(props) => <Popper {...props} placement="bottom-start" />}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                sx={MuiTextFieldWithFocus}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {yearLoading ? <CircularProgress size={16} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Make */}
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="body1" sx={labelSx}>
                                        Make
                                    </Typography>
                                    <Autocomplete
                                        fullWidth
                                        disableClearable
                                        forcePopupIcon
                                        disabled={!draftYear}
                                        loading={makeLoading}
                                        options={makeList}
                                        getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
                                        isOptionEqualToValue={(option, value) => option._id === value?._id}
                                        value={makeList.find((m) => m._id === draftMake) || null}
                                        inputValue={makeInputValue}
                                        onChange={(event, newValue) => {
                                            handleMakeChange(newValue);
                                        }}
                                        onInputChange={(event, newValue) => {
                                            setMakeInputValue(newValue);
                                        }}
                                        ListboxProps={{
                                            onScroll: (e) => handleScroll(e, 'make'),
                                            style: { maxHeight: 220 },
                                        }}
                                        PopperComponent={(props) => <Popper {...props} placement="bottom-start" />}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                disabled={!draftYear}
                                                sx={MuiTextFieldWithFocus}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {makeLoading ? <CircularProgress size={16} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Model */}
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="body1" sx={labelSx}>
                                        Model
                                    </Typography>
                                    <Autocomplete
                                        fullWidth
                                        disableClearable
                                        forcePopupIcon
                                        disabled={!draftYear || !draftMake}
                                        loading={modelLoading}
                                        options={modelList}
                                        getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
                                        isOptionEqualToValue={(option, value) => option._id === value?._id}
                                        value={modelList.find((m) => m._id === draftModel) || null}
                                        inputValue={modelInputValue}
                                        onChange={(event, newValue) => {
                                            handleModelChange(newValue);
                                        }}
                                        onInputChange={(event, newValue) => {
                                            setModelInputValue(newValue);
                                        }}
                                        ListboxProps={{
                                            onScroll: (e) => handleScroll(e, 'model'),
                                            style: { maxHeight: 220 },
                                        }}
                                        PopperComponent={(props) => <Popper {...props} placement="bottom-start" />}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                disabled={!draftYear || !draftMake}
                                                sx={MuiTextFieldWithFocus}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {modelLoading ? <CircularProgress size={16} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box
                            sx={{
                                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                                mt: 2,
                                py: 2,
                                px: 3,
                                mb: 2,
                                display: 'flex',
                                justifyContent: 'flex-end',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Stack spacing={2} direction="row">
                                <Button variant="outlined" color="primary" onClick={handleResetAndApply}>
                                    RESET
                                </Button>
                                <Button variant="contained" color="primary" type="submit">
                                    DONE
                                </Button>
                            </Stack>
                        </Box>
                    </form>
                </Box>
            </Modal>
        </Box>
    );
};

export default SelectVehicleModal;
